import { siteInfo, aboutContent, faqData } from '@/mocks/site';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';

export default function About() {
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteInfo.name,
    url: siteInfo.url,
    description: siteInfo.description,
    email: siteInfo.email,
    foundingDate: siteInfo.foundingYear,
    address: {
      '@type': 'PostalAddress',
      streetAddress: siteInfo.address,
      addressLocality: '北京',
      addressRegion: '朝阳区',
      addressCountry: 'CN',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: siteInfo.email,
    },
    sameAs: [
      'https://weibo.com/jinriheiliao',
    ],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  const aboutPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: `关于我们 - ${siteInfo.name}`,
    description: `了解${siteInfo.name}的品牌故事、团队介绍与联系方式`,
    url: `${siteInfo.url}/about`,
    isPartOf: {
      '@type': 'WebSite',
      name: siteInfo.name,
      url: siteInfo.url,
    },
  };

  return (
    <>
      <SEOHead
        title={`关于我们 - ${siteInfo.name}`}
        description={`了解${siteInfo.name}的品牌故事、团队介绍与联系方式。${siteInfo.description}`}
        keywords={`关于${siteInfo.name},${siteInfo.name}品牌,联系方式`}
        canonicalUrl={`${siteInfo.url}/about`}
        schema={{ ...organizationSchema, ...faqSchema, ...aboutPageSchema }}
      />

      <main>
        <Breadcrumb
          items={[
            { label: '首页', to: '/' },
            { label: '关于我们' },
          ]}
        />

        {/* Hero */}
        <section className="relative bg-foreground-950 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/80 to-foreground-950"></div>
          <div className="relative px-4 md:px-6 py-16 md:py-20 max-w-7xl mx-auto text-center">
            <h1 className="text-3xl md:text-4xl font-bold text-background-50 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
              关于今日黑料
            </h1>
            <p className="text-background-50/60 max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
              {siteInfo.description}
            </p>
          </div>
        </section>

        {/* About Sections */}
        <section className="px-4 md:px-6 py-12 max-w-4xl mx-auto">
          <div className="space-y-12">
            {aboutContent.sections.map((section) => (
              <div key={section.id}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-1 h-6 bg-accent-500 rounded-full"></div>
                  <h2 className="text-xl font-semibold text-foreground-900">{section.title}</h2>
                </div>
                <div
                  className="prose prose-base max-w-none text-foreground-700 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: section.content }}
                />
              </div>
            ))}
          </div>
        </section>

        {/* Contact Info */}
        <section className="px-4 md:px-6 py-12 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <div className="w-1 h-6 bg-accent-500 rounded-full mx-auto mb-3"></div>
              <h2 className="text-xl font-semibold text-foreground-900">联系我们</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div className="bg-background-50 rounded-lg border border-background-200 p-6 text-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-500/10 mx-auto mb-3">
                  <i className="ri-mail-line text-accent-500 text-xl"></i>
                </div>
                <h3 className="text-sm font-medium text-foreground-800 mb-1">电子邮箱</h3>
                <p className="text-sm text-foreground-500">{siteInfo.email}</p>
              </div>
              <div className="bg-background-50 rounded-lg border border-background-200 p-6 text-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-500/10 mx-auto mb-3">
                  <i className="ri-wechat-line text-accent-500 text-xl"></i>
                </div>
                <h3 className="text-sm font-medium text-foreground-800 mb-1">微信公众号</h3>
                <p className="text-sm text-foreground-500">{siteInfo.wechat}</p>
              </div>
              <div className="bg-background-50 rounded-lg border border-background-200 p-6 text-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-500/10 mx-auto mb-3">
                  <i className="ri-weibo-line text-accent-500 text-xl"></i>
                </div>
                <h3 className="text-sm font-medium text-foreground-800 mb-1">官方微博</h3>
                <p className="text-sm text-foreground-500">{siteInfo.weibo}</p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="px-4 md:px-6 py-12 max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <div className="w-1 h-6 bg-accent-500 rounded-full mx-auto mb-3"></div>
            <h2 className="text-xl font-semibold text-foreground-900">常见问题</h2>
          </div>
          <div className="space-y-3">
            {faqData.map((faq, index) => (
              <details
                key={index}
                className="bg-background-50 rounded-lg border border-background-200 group"
              >
                <summary className="flex items-center justify-between px-5 py-4 cursor-pointer text-foreground-800 font-medium text-sm group-open:text-accent-500 transition-colors">
                  {faq.question}
                  <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                </summary>
                <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                  {faq.answer}
                </div>
              </details>
            ))}
          </div>
        </section>
      </main>
    </>
  );
}